/**
 * components/card.tsx — the design's `.t-card` (+ head / body).
 * Composed freely: <Card><CardHead title="…" right={…}/><CardBody>…</CardBody></Card>.
 */
import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { LightColors as C, Radii as R, Shadows as SH, FontSize as FS, FontWeight as FW } from '../theme/theme';
import { Txt } from './ui';

export const Card: React.FC<{ children?: React.ReactNode; style?: StyleProp<ViewStyle> }> = ({ children, style }) => (
  <View
    style={[
      {
        backgroundColor: C.card,
        borderWidth: 1,
        borderColor: C.line,
        borderRadius: R.r3,
        overflow: 'hidden',
      },
      SH.sm as ViewStyle,
      style,
    ]}
  >
    {children}
  </View>
);

export const CardHead: React.FC<{
  title?: string;
  right?: React.ReactNode;
  /** Tinted head (builder live-preview uses accent-soft). */
  accent?: boolean;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}> = ({ title, right, accent = false, children, style }) => (
  <View
    style={[
      {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: 1,
        borderBottomColor: accent ? C.accentLine : C.line,
        backgroundColor: accent ? C.accentSoft : 'transparent',
      },
      style,
    ]}
  >
    {title !== undefined ? (
      <Txt size={FS.md} weight={FW.semibold} color={accent ? C.accentInk : C.ink}>
        {title}
      </Txt>
    ) : (
      children
    )}
    {right}
  </View>
);

export const CardBody: React.FC<{ children?: React.ReactNode; style?: StyleProp<ViewStyle> }> = ({
  children,
  style,
}) => <View style={[{ padding: 16 }, style]}>{children}</View>;
